<template>
  <FiltersDrawerSkeletonLoader
    v-if="isLoading"
    :show-my-jobs="filterConfig.showMyJobs"
  />
  <div v-else class="filter-container bg-bgDefault">
    <ExpandableFiltersMainHeader
      v-if="!hideFilters"
      :are-filters-applied="areNonIgnorableFiltersApplied"
      :tooltip="tooltips['filters-general']"
      @clear-filters="clearFilters"
    />
    <MyJobs
      v-if="filterConfig.showMyJobs"
      :is-loading="isLoading"
      :show-my-jobs="filterConfig.showMyJobs"
      :enforce-my-jobs-enabled="enforcedMyJobsEnabled"
      @filters-tracking-event="handleTrackingEvent"
    />
    <ExpandableFilterCategories
      v-if="!hideFilters"
      :categories="categoryHeaders"
      :filters="categoriesWithoutCustomFilters"
      :original-filters="fields"
      :ats-id="authData.atsId"
      :tooltips="tooltips"
      @reset-values="resetValues"
      @set-temp-filters="setTempFilters"
      @apply-filters="applyFilters"
      @clear-filters="clearFilters"
      @add-snackbar="addErrorSnackbar"
    />
    <CustomFilters
      v-if="!hideFilters && showCustomFilters"
      :filters="categories.custom"
      :original-filters="fields"
      :tooltip="tooltips['filters-custom']"
      @set-temp-filters="setTempFilters($event)"
      @add-snackbar="addErrorSnackbar"
      @clear-filters="clearFilters"
      @clear-multiple-filters="clearMultipleValues"
      @apply-filters="applyFilters"
      @reset-values="resetValues"
      @reset-multiple-values="resetMultipleValues"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useFilters } from '@/stores/filters/filters';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppBase } from '@/stores/app/base';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { useTracking } from '@/stores/tracking/tracking';
import { useTooltips } from '@/stores/insights/tooltips';
import FiltersDrawerSkeletonLoader from '@/components/shared/SkeletonLoaders/FiltersDrawerSkeletonLoader.vue';
import MyJobs from '@/components/filter/MyJobs.vue';
import ExpandableFilterCategories from '@/components/filter/ExpandableFilterCategories.vue';
import ExpandableFiltersMainHeader from '@/components/filter/ExpandableFiltersMainHeader.vue';
import CustomFilters from '@/components/filter/customFilters/CustomFilters.vue';

const route = useRoute();
const router = useRouter();

// Stores
const authDataStore = useAppAuthData();
const { authData } = storeToRefs(authDataStore);

const appBaseStore = useAppBase();
const { pageHidesFilterChips } = storeToRefs(appBaseStore);

const filtersStore = useFilters();
const {
  fields,
  tempFilters,
  enforcedMyJobsEnabled,
  areNonIgnorableFiltersApplied,
  trackingFilters,
  filterConfig,
} = storeToRefs(filtersStore);
const {
  resetValues,
  resetMultipleValues,
  setTempFilters,
  clearValues,
  clearMultipleValues,
  sendFilterRequest,
} = filtersStore;

const loadingStore = useAppLoading();
const { pageLoading, filtersLoading } = storeToRefs(loadingStore);

const { addErrorSnackbar } = useAppSnackbar();

const tooltipStore = useTooltips();
const { tooltips } = storeToRefs(tooltipStore);

const categoriesWithNames = {
  basic: {
    key: 'basic',
    name: '',
    className: '',
    defaultIcon: false,
    show: false,
    position: 0,
  },
  tag: {
    key: 'tag',
    name: 'Datapeople Filters',
    className: 'tag',
    defaultIcon: true,
    show: true,
    position: 1,
    tooltipKey: 'filters-datapeople',
  },
  ats: {
    key: 'ats',
    name: `${authData.value.atsName || 'ATS'} Filters`,
    className: 'ats',
    defaultIcon: false,
    show: true,
    position: 2,
    tooltipKey: 'filters-ats',
  },
  custom: {
    key: 'custom',
    name: 'Custom Filters',
    className: 'custom',
    defaultIcon: false,
    show: false,
    position: 3,
    tooltipKey: 'filters-custom',
  },
};

const isLoading = computed(() => pageLoading.value || filtersLoading.value);

// Hide the actual filters - My Jobs is not apart of actual filters
const hideFilters = computed(() => pageHidesFilterChips.value[route.name]);

const categories = computed(() => tempFilters.value.reduce((acc, item, index) => {
  const ignored = filterConfig.value.ignorableFilters || [];

  if (ignored.includes(item.id)) {
    return acc;
  }

  const bucket = item.category || 'basic';
  if (!acc[bucket]) acc[bucket] = [];

  acc[bucket].push({ ...item, originalIndex: index });
  return acc;
}, {}));

const categoriesWithoutCustomFilters = computed(() => {
  const categoriesCopy = { ...categories?.value };
  if (categoriesCopy.custom) {
    delete categoriesCopy.custom;
  }

  return categoriesCopy;
});

// Custom filters are tucked away in the modal, that modal only holds
// custom filters. The trigger to open the modal is in the drawer as well
const showCustomFilters = computed(() => categories?.value.custom);

// Accordion panels don't need custom filters
const categoryHeaders = computed(() => {
  const categoryKeys = Object.keys(categoriesWithoutCustomFilters.value);
  return categoryKeys
    .map((category) => categoriesWithNames[category])
    .sort((a, b) => a.position - b.position);
});

const handleTrackingEvent = ({ action, args }) => {
  useTracking()[action](args);
};

const applyFilters = () => {
  handleTrackingEvent({
    action: 'filterApply',
    args: { filters: trackingFilters.value, page: route.name },
  });

  // New Events in testing
  handleTrackingEvent({
    action: 'appliedFilters',
    args: { page: route.name, filterTypes: trackingFilters.value },
  });
  sendFilterRequest({ router, resetData: true });
};

const clearFilters = ({ index, router: routerFromState }) => {
  handleTrackingEvent({
    action: 'filterClear',
    args: {
      filter: fields.value[index]?.label || 'all',
      page: route.name,
    },
  });
  clearValues({ index, router: routerFromState });
};
</script>

<style lang="less" scoped>
.filter-container {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
